import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_LABEL = {
  requested: '신청완료', approved: '판매자 승인', rejected: '거절됨',
  pickup_pending: '회수대기', pickup_in_progress: '회수중',
  received: '입고완료', inspecting: '검수중', inspected: '검수완료',
  processing: '처리중', exchange_preparing: '교환상품 준비',
  exchange_shipped: '교환 발송', completed: '완료', cancelled: '취소',
};
const REFUND_LABEL = { NOT_REQUIRED: '해당없음', PENDING: '환불 대기',
  READY: '환불 준비', PROCESSING: '환불 처리중',
  COMPLETED: '환불 완료', FAILED: '환불 실패' };

const TABS = [['all', '전체'], ['return', '반품'], ['exchange', '교환'],
  ['requested', '승인대기'], ['pickup_pending', '회수중'],
  ['inspecting', '검수중'], ['completed', '처리완료']];

export default function SellerClaims() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      // Reuse existing seller order-level claims endpoint per order · here we
      // aggregate by listing recent orders and pulling claims — for simplicity
      // fall back to /api/seller/rma if present. This is a minimal Foundation
      // view; production dashboards would use a dedicated seller claims list.
      const r = await api.get('/api/seller/rma').catch(() => ({ data: { items: [] } }));
      setRows(r.data.items || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (tab === 'all') return true;
    if (['return', 'exchange'].includes(tab)) return r.type === tab;
    return r.status === tab;
  }), [rows, tab]);

  const openDetail = async (rmaId) => {
    try { const r = await api.get(`/api/seller/claims/${rmaId}`);
      setDetail(r.data); } catch { toast.error('상세를 불러오지 못했습니다.'); }
  };

  const doApprove = async () => {
    if (!detail) return; setBusy(true);
    try {
      await api.patch(`/api/seller/claims/${detail.rma_id}/approve`, {});
      toast.success('승인되었습니다.'); await openDetail(detail.rma_id); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '승인 실패'); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    const reason = window.prompt('거절 사유를 입력하세요:');
    if (!reason) return; setBusy(true);
    try {
      await api.patch(`/api/seller/claims/${detail.rma_id}/reject`,
        { rejection_reason: reason });
      toast.success('거절되었습니다.'); await openDetail(detail.rma_id); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '거절 실패'); }
    finally { setBusy(false); }
  };

  const setPickup = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/api/seller/claims/${detail.rma_id}/pickup`, payload);
      toast.success('저장되었습니다.'); await openDetail(detail.rma_id);
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="seller-claims-page">
        <h1 className="text-lg font-semibold mb-4">반품/교환 관리</h1>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="seller-claims-tabs">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
                     className={`px-3 py-1 text-sm rounded ${tab === v ? 'bg-neutral-900 text-white' : 'bg-neutral-100'}`}
                     data-testid={`seller-claims-tab-${v}`}>{l}</button>
          ))}
        </div>

        <div className="border rounded divide-y" data-testid="seller-claims-list">
          {filtered.length === 0 && <div className="p-4 text-sm text-neutral-500">해당 항목이 없습니다.</div>}
          {filtered.map(r => (
            <button key={r.rma_id} onClick={() => openDetail(r.rma_id)}
                     className="w-full text-left p-3 hover:bg-neutral-50 flex items-center justify-between"
                     data-testid={`seller-claim-row-${r.rma_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{r.rma_id} · {r.type}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  주문 {r.seller_order_id} · {r.items?.length || 0}개 상품
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-neutral-100 rounded">{STATUS_LABEL[r.status] || r.status}</span>
            </button>
          ))}
        </div>

        {detail && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
                onClick={() => setDetail(null)} data-testid="seller-claim-detail-modal">
            <div className="bg-white rounded max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
                  onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-neutral-500">Claim {detail.rma_id}</div>
                  <div className="text-lg font-semibold">
                    {detail.type === 'return' ? '반품' : '교환'} · {STATUS_LABEL[detail.status] || detail.status}
                  </div>
                </div>
                <button onClick={() => setDetail(null)} className="text-neutral-500"
                         data-testid="seller-claim-close">✕</button>
              </div>
              <dl className="grid grid-cols-2 gap-2 text-sm mb-4">
                <dt className="text-neutral-500">주문번호</dt><dd>{detail.seller_order_id}</dd>
                <dt className="text-neutral-500">사유</dt><dd>{detail.reason_code}</dd>
                <dt className="text-neutral-500">회수방법</dt><dd>{detail.pickup_method || '-'}</dd>
                <dt className="text-neutral-500">회수 송장</dt><dd>{detail.return_tracking_number || '-'}</dd>
                <dt className="text-neutral-500">배송비 부담</dt><dd>{detail.shipping_fee_payer || 'UNDECIDED'}</dd>
                <dt className="text-neutral-500">환불 상태</dt><dd data-testid="seller-claim-refund-status">
                  {REFUND_LABEL[detail.refund_status] || detail.refund_status || '-'}</dd>
                <dt className="text-neutral-500">검수 결과</dt><dd>{detail.inspection_result || '-'}</dd>
                <dt className="text-neutral-500">교환 재출고</dt><dd>
                  {detail.exchange_shipment?.tracking_number || '-'}</dd>
              </dl>

              <div className="text-xs text-neutral-500 mb-1">상품</div>
              <div className="border rounded divide-y mb-3 text-sm">
                {(detail.items || []).map((it, i) => (
                  <div key={i} className="p-2 flex justify-between">
                    <span>{it.name_snapshot || it.product_id}</span>
                    <span>{it.claim_quantity} / {it.ordered_quantity}</span>
                  </div>
                ))}
              </div>

              {detail.status === 'requested' && (
                <div className="flex gap-2 mb-3">
                  <button onClick={doApprove} disabled={busy}
                           className="flex-1 py-2 bg-emerald-600 text-white text-sm rounded disabled:opacity-50"
                           data-testid="seller-claim-approve">승인</button>
                  <button onClick={doReject} disabled={busy}
                           className="flex-1 py-2 bg-rose-600 text-white text-sm rounded disabled:opacity-50"
                           data-testid="seller-claim-reject">거절</button>
                </div>
              )}
              {detail.status === 'approved' && (
                <div className="space-y-2 border-t pt-3">
                  <div className="text-xs text-neutral-500">수동 회수 정보</div>
                  <input type="text" placeholder="택배사명(직접입력 가능)"
                          className="border rounded px-2 py-1 w-full text-sm"
                          data-testid="seller-manual-courier"
                          onBlur={(e) => e.target.value &&
                            setPickup({ pickup_method: 'CUSTOMER_SEND',
                              carrier_account_id: null })} />
                  <input type="text" placeholder="반품 송장번호"
                          className="border rounded px-2 py-1 w-full text-sm"
                          data-testid="seller-manual-tracking"
                          onBlur={(e) => e.target.value &&
                            setPickup({ return_tracking_number: e.target.value })} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
